import { useState } from 'react';

import { Navigate } from '@tanstack/react-router';

import { AlertTriangle, Check, CreditCard } from 'lucide-react';

import { Button, cn } from '@src/components/ui';
import { PLAN_FEATURES, PLAN_ORDER } from '@src/constants';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import {
  useBillingInterchangeMutation,
  useBillingPlanMutation,
  useBillingQuery,
  useMineQuery,
  useOptionLabels,
} from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import { ConfirmDialog, firstApiError, Switch } from './controls';
import PageHeader from './PageHeader';


const MS_PER_DAY = 24 * 60 * 60 * 1000;


/** Returns a stable `(plan) => label` resolver backed by the metadata-options
 * hook (#364), preserving the prior capitalize/"Free" fallback. MUST be called
 * at component top level. */
function usePlanLabel() {
  const labels = useOptionLabels('subscription_plan');
  return (plan) => labels[plan] || (plan ? `${plan[0].toUpperCase()}${plan.slice(1)}` : 'Free');
}


function trialDaysLeft(trialEndsAt) {
  if (!trialEndsAt) {
    return null;
  }
  const left = Math.ceil((new Date(trialEndsAt) - Date.now()) / MS_PER_DAY);
  return left > 0 ? left : 0;
}


function formatMoney(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) {
    return '—';
  }
  return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
}


function formatDate(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}


/** Top card: state-aware banner for trialing / active / expired / past_due. */
function StatusCard({ subscription, daysLeft }) {
  const planLabel = usePlanLabel();
  const status = subscription?.effective_status;

  const CONFIG = {
    trialing: {
      frame: 'bg-primary/6 border-primary/20',
      iconBox: 'bg-brand-gradient text-white',
      icon: CreditCard,
      title: daysLeft === 0
        ? 'Free trial ended'
        : `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
      subtitle: 'Everything unlocked. Choose a plan to keep going.',
    },
    active: {
      frame: 'bg-success/8 border-success/25',
      iconBox: 'bg-success/15 text-success',
      icon: Check,
      title: `${planLabel(subscription?.plan)} plan — active`,
      subtitle: 'Your subscription is current. Manage your plan and add-ons below.',
    },
    expired: {
      frame: 'bg-danger/8 border-danger/30',
      iconBox: 'bg-danger/15 text-danger',
      icon: AlertTriangle,
      title: 'Subscription expired',
      subtitle: 'Reads still work, but changes are locked. Choose a plan to restore full access.',
    },
    past_due: {
      frame: 'bg-danger/8 border-danger/30',
      iconBox: 'bg-danger/15 text-danger',
      icon: AlertTriangle,
      title: 'Payment past due',
      subtitle: 'Changes are locked until billing is updated. Re-activate a plan below.',
    },
  };

  const config = CONFIG[status] || CONFIG.active;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'flex items-center gap-3.5 rounded-xl border px-[18px] py-4',
        config.frame,
      )}
    >
      <span className={cn('grid size-[38px] shrink-0 place-items-center rounded-[10px]', config.iconBox)}>
        <Icon className="size-[18px]" />
      </span>

      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[14.5px] font-semibold text-ink">{config.title}</span>
        <span className="text-[12.5px] text-ink-muted">{config.subtitle}</span>
      </span>
    </div>
  );
}

StatusCard.displayName = 'StatusCard';


function PlanCard({ plan, price, seatCap, isCurrent, popular, disabled, onSelect }) {
  const planLabel = usePlanLabel();
  return (
    <div
      className={cn(
        'flex flex-1 flex-col gap-3 rounded-[14px] bg-surface-1 p-[18px]',
        popular ? 'border-2 border-primary' : 'border border-hairline',
      )}
    >
      <span className="flex items-center justify-between">
        <span className="text-[15px] font-semibold text-ink">{planLabel(plan)}</span>

        {popular
          ? (
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary">
              Popular
            </span>
          )
          : null}
      </span>

      <span className="flex items-end gap-[3px]">
        <span
          className={cn(
            'text-[28px] font-bold leading-none tracking-[-1px]',
            popular ? 'text-primary' : 'text-ink',
          )}
        >
          {formatMoney(price)}
        </span>

        <span className="text-[13px] text-ink-subtle">/mo</span>
      </span>

      <span className="text-[12.5px] font-medium text-ink-muted">{`${seatCap} seats`}</span>

      <span className="flex flex-1 flex-col gap-[7px]">
        {(PLAN_FEATURES[plan] || []).map((feature) => (
          <span key={feature} className="flex items-center gap-[7px]">
            <Check className="size-3.5 shrink-0 text-success" strokeWidth={2.5} />
            <span className="text-[12.5px] text-ink-muted">{feature}</span>
          </span>
        ))}
      </span>

      {isCurrent
        ? (
          <span
            className={cn(
              'flex h-10 items-center justify-center rounded-lg border border-hairline-strong',
              'bg-surface-2 text-[13.5px] font-semibold text-ink-muted',
            )}
          >
            Current plan
          </span>
        )
        : (
          <Button
            type="button"
            disabled={disabled}
            className="h-10 text-[13.5px]"
            variant={popular ? 'primary' : 'secondary'}
            onClick={onSelect}
          >
            {popular ? `Choose ${planLabel(plan)}` : 'Select'}
          </Button>
        )}
    </div>
  );
}

PlanCard.displayName = 'PlanCard';


const INVOICE_COLUMNS = [
  ['Invoice', 'flex-1'],
  ['Date', 'w-28'],
  ['Plan', 'w-24'],
  ['Amount', 'w-20'],
  ['Status', 'w-24'],
];


function InvoicesTable({ invoices }) {
  const planLabel = usePlanLabel();
  if (invoices.length === 0) {
    return (
      <span
        className={cn(
          'rounded-[10px] border border-hairline bg-surface-1 px-4 py-5 text-center text-[13px] text-ink-subtle',
        )}
      >
        No invoices yet.
      </span>
    );
  }

  return (
    <div className="overflow-hidden rounded-[10px] border border-hairline bg-surface-1">
      <div className="flex h-9 items-center gap-3 border-b border-hairline px-4">
        {INVOICE_COLUMNS.map(([label, width]) => (
          <span
            key={label}
            className={cn('text-[10px] font-semibold uppercase tracking-[0.3px] text-ink-subtle', width)}
          >
            {label}
          </span>
        ))}
      </div>

      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className="flex items-center gap-3 border-b border-hairline px-4 py-2.5 last:border-0"
        >
          <span className="flex-1 truncate text-[13px] font-medium text-ink">{invoice.number}</span>
          <span className="w-28 text-[12.5px] text-ink-muted">{formatDate(invoice.issued_at)}</span>
          <span className="w-24 text-[12.5px] text-ink-muted">{planLabel(invoice.plan)}</span>
          <span className="w-20 text-[12.5px] font-medium text-ink">{formatMoney(invoice.amount)}</span>

          <span className="w-24">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                invoice.status === 'paid' ? 'bg-success/15 text-success' : 'bg-warning/15 text-warning',
              )}
            >
              {invoice.status === 'paid' ? 'Paid' : invoice.status}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

InvoicesTable.displayName = 'InvoicesTable';


/** Billing & plan (#160, #161, .pen h0moNd): state-aware status card, plan
 * picker (simulated checkout), interchange add-on, seats, demo payment method,
 * invoices. Owner only — other roles are redirected. Demo billing — no
 * payment processor. */
function Billing() {
  const { toasts, push } = useToasts();
  const planLabel = usePlanLabel();
  // Mine query retained only for its cache key (plan changes invalidate the org).
  const { queryKey: mineKey } = useMineQuery();
  const { seller } = useActiveOrg();
  const { canBilling: isOwner } = usePermissions();

  const billingQuery = useBillingQuery(seller?.id, { enabled: Boolean(seller) && isOwner });
  const queryKey = billingQuery.queryKey;

  const [pendingPlan, setPendingPlan] = useState(null);

  const planMutation = useBillingPlanMutation();
  const interchangeMutation = useBillingInterchangeMutation();

  if (!seller) {
    return null;
  }

  if (!isOwner) {
    return <Navigate replace to="/app/settings" />;
  }

  const billing = billingQuery.data;
  const subscription = billing?.subscription;
  const seats = billing?.seats;
  const plans = billing?.plans || [];
  const invoices = billing?.invoices || [];
  const currentPlan = subscription?.plan;
  const daysLeft = trialDaysLeft(subscription?.trial_ends_at);
  const interchangeOn = Boolean(subscription?.interchange_addon);

  const planByKey = Object.fromEntries(plans.map((entry) => [entry.plan, entry]));

  const confirmPlan = () => {
    const plan = pendingPlan;
    planMutation.mutate(
      { sellerId: seller.id, plan, queryKey, mineKey },
      {
        onSuccess: () => {
          setPendingPlan(null);
          push(`Switched to ${planLabel(plan)}`);
        },
        onError: (error) => {
          setPendingPlan(null);
          push(firstApiError(error?.response?.data, 'plan') || 'Could not change plan', 'danger');
        },
      },
    );
  };

  const toggleInterchange = () => {
    interchangeMutation.mutate(
      { sellerId: seller.id, enabled: !interchangeOn, queryKey, mineKey },
      { onError: () => push('Could not update add-on', 'danger') },
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader title="Billing & plan" subtitle="Manage your subscription and add-ons." />

      <div className="flex w-full flex-col gap-[18px] px-4 pb-8 lg:px-0">
        {billingQuery.isPending
          ? <span className="py-10 text-center text-[13px] text-ink-subtle">Loading billing…</span>
          : billingQuery.isError
            ? <span className="py-10 text-center text-[13px] text-danger">Could not load billing.</span>
            : (
              <>
                <StatusCard daysLeft={daysLeft} subscription={subscription} />
                {/* Plans */}
                <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">Plans</span>

                <div className="flex flex-col gap-3.5 lg:flex-row">
                  {PLAN_ORDER.map((plan) => {
                    const entry = planByKey[plan];
                    if (!entry) {
                      return null;
                    }
                    return (
                      <PlanCard
                        key={plan}
                        plan={plan}
                        price={entry.price}
                        seatCap={entry.seat_cap}
                        isCurrent={plan === currentPlan}
                        disabled={planMutation.isPending}
                        popular={plan === 'shop'}
                        onSelect={() => setPendingPlan(plan)}
                      />
                    );
                  })}
                </div>

                <span className="text-[12px] text-ink-tertiary">
                  Demo billing — selecting a plan activates instantly with no payment processor.
                </span>

                {/* Add-ons */}
                <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Add-ons
                </span>

                <div className="flex items-center gap-3 rounded-[10px] border border-hairline bg-surface-1 px-4 py-3">
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-[13.5px] font-semibold text-ink">Fitment data (interchange)</span>

                    <span className="text-xs text-ink-subtle">
                      Unlock &apos;what else this part fits&apos; suggestions
                    </span>
                  </span>

                  {/* Backend INTERCHANGE_ADDON_PRICE = 49; not in the billing payload, so kept in sync here. */}
                  <span className="text-[13px] font-semibold text-ink-muted">+$49/mo</span>

                  <Switch
                    checked={interchangeOn}
                    disabled={interchangeMutation.isPending}
                    label={`${interchangeOn ? 'Disable' : 'Enable'} interchange add-on`}
                    onChange={toggleInterchange}
                  />
                </div>

                {/* Seats */}
                {seats
                  ? (
                    <div
                      className={cn(
                        'flex flex-wrap items-center gap-2.5 rounded-[10px] border border-hairline',
                        'bg-surface-1 px-4 py-3',
                      )}
                    >
                      <span className="text-[13.5px] font-semibold text-ink">
                        {`${seats.used} of ${seats.cap} seats used`}
                      </span>

                      <span className="text-[12.5px] text-ink-subtle">{`${planLabel(currentPlan)} plan`}</span>
                    </div>
                  )
                  : null}

                {/* Payment method (demo placeholder) */}
                <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Payment method
                </span>

                <div className="flex items-center gap-3 rounded-[10px] border border-hairline bg-surface-1 px-4 py-3.5">
                  <span
                    className={cn(
                      'grid size-[38px] shrink-0 place-items-center rounded-[9px] bg-surface-2 text-ink-subtle',
                    )}
                  >
                    <CreditCard className="size-[18px]" />
                  </span>

                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-[13.5px] font-semibold text-ink">No card on file</span>
                    <span className="text-xs text-ink-subtle">Demo billing — no payment processor connected.</span>
                  </span>
                </div>

                {/* Invoices */}
                <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-ink-subtle">
                  Invoices
                </span>

                <InvoicesTable invoices={invoices} />
              </>
            )}
      </div>

      {pendingPlan
        ? (
          <ConfirmDialog
            variant="primary"
            busy={planMutation.isPending}
            title={`Switch to ${planLabel(pendingPlan)}?`}
            confirmLabel="Activate"
            onConfirm={confirmPlan}
            onCancel={() => setPendingPlan(null)}
            body="Demo billing — this activates instantly with no payment processor. You can change plans anytime."
          />
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

Billing.displayName = 'Billing';
export default Billing;
